import { useId, useState } from 'react';

import classNames from 'classnames';

import type { AnchorLinkTargets } from '../AnchorLink';
import type { NotificationPanelVariants } from '../NotificationPanel';

import { useBreakpoint } from '../../hooks/useBreakpoint';
import { usePseudoClasses } from '../../hooks/usePseudoClasses';
import { getColor } from '../../theme/currys';
import { breakpoints } from '../../theme/grid';
import { getAriaLabelAttributes } from '../../utils/accessibility';
import NotificationBadge from '../Badge/NotificationBadge';
import Close from '../Close';
import { renderElementHeader } from '../ElementHeader/utils';
import Icon, { IconSize } from '../Icon';
import ChevronDown from '../Icons/ChevronDown';
import ChevronUp from '../Icons/ChevronUp';
import Forward from '../Icons/Forward';
import X from '../Icons/X';

import styles from './styles.module.scss';

interface LabelProps {
  label: string;
  variant: NotificationPanelVariants;
  id: string;
  hasExpander: boolean;
  isExpanded: boolean;
  dismissable: boolean;
  onExpand: () => void;
  onDismiss?: () => void;
  closeBtnText?: string;
}

const Label: React.FC<LabelProps> = ({ label, variant, id, hasExpander, isExpanded, dismissable, onExpand, onDismiss, closeBtnText }) => {
  const breakpoint = useBreakpoint();
  const { isHovered, refObject } = usePseudoClasses<HTMLDivElement>();

  const iconSize = breakpoint < breakpoints.lg ? IconSize.XSmall : IconSize.Small;
  const labelContainerClasses = classNames(
    styles['service-message__label-container'],
    styles[`service-message__label-container--${variant}`],
    hasExpander && styles[`service-message__label-container--has-expander`]
  );

  const elementHeader = renderElementHeader(label, {
    titleHtmlMarkup: 'span',
    isHovered,
    size: 'compact',
    parentType: 'expanderlist',
    chevronIcon: hasExpander ? (isExpanded ? ChevronUp : ChevronDown) : undefined,
    icon: <NotificationBadge variant={variant} size={iconSize} isHovered={hasExpander && isHovered} />,
    closeButton:
      !hasExpander && dismissable ? (
        <Close onClick={onDismiss} ariaLabel={closeBtnText} className={styles['service-message__close']} />
      ) : undefined,
  });

  return (
    <div className={labelContainerClasses} ref={refObject}>
      <div className={styles['service-message__container']}>
        <div className={styles['service-message__row']}>
          <div className={styles['service-message__col']}>
            <div className={styles['service-message__label']}>
              {hasExpander ? (
                <h1 className={styles['service-message__title']} id={id}>
                  <button type="button" className={styles['service-message__toggle']} onClick={onExpand} aria-expanded={isExpanded}>
                    {elementHeader}
                  </button>
                </h1>
              ) : (
                <h1 className={styles['service-message__title']} id={id}>
                  {elementHeader}
                </h1>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ContentProps {
  info?: string;
  extraInfo?: string;
  urlTitle?: string;
  url?: string;
  target?: AnchorLinkTargets;
  dismissable: boolean;
  onDismiss?: () => void;
  closeBtnText?: string;
}

const Content: React.FC<ContentProps> = ({ info, extraInfo, urlTitle, url, target, dismissable, closeBtnText, onDismiss }) => {
  const { refObject: readMoreRef, isHovered: readMoreHoverRefIsHovered } = usePseudoClasses<HTMLAnchorElement>();
  const { refObject: closeButtonRef, isHovered: closeButtonIsHovered } = usePseudoClasses<HTMLButtonElement>();

  const hasUrl = url && urlTitle;

  return (
    <div className={styles['service-message__container']}>
      <div className={styles['service-message__row']}>
        <div className={styles['service-message__col']}>
          <div className={styles['service-message__content']}>
            {info && <p className={styles['service-message__info']}>{info}</p>}
            {extraInfo && (
              <p className={classNames(styles['service-message__info'], styles['service-message__info--extra'])}>{extraInfo}</p>
            )}
            <div className={styles['service-message__actions']}>
              {hasUrl && (
                <a className={styles['service-message__action']} href={url} ref={readMoreRef} target={target}>
                  {urlTitle}
                  <Icon size={IconSize.XSmall} svgIcon={Forward} color={getColor('blueberry', 700)} isHovered={readMoreHoverRefIsHovered} />
                </a>
              )}

              {dismissable && (
                <button
                  ref={closeButtonRef}
                  className={classNames(styles['service-message__action'], styles['service-message__action--close'])}
                  aria-label={closeBtnText}
                  onClick={onDismiss}
                >
                  {closeBtnText}
                  <Icon size={IconSize.XSmall} svgIcon={X} color={getColor('blueberry', 700)} isHovered={closeButtonIsHovered} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export type ServiceMessageRoles = 'alert' | 'region' | 'auto' | 'none';

export interface ServiceMessageProps {
  /** Sets a label for the notification panel. */
  label: string;
  /** String displayed in service-message when expanded */
  info?: string;
  /** String displayed in service-message when expanded, with a smaller font */
  extraInfo?: string;
  /** function that runs on dismiss */
  onDismiss?: () => void;
  /** Allows for dismiss to be an option, also for ServiceMessage with only label as content */
  dismissable?: boolean;
  /** Makes expander be open from start. */
  expanderOpenFromStart?: boolean;
  /** Name that describes a url-link for the user*/
  urlTitle?: string;
  /** Url for further information*/
  url?: string;
  /** Sets target for the anchorlink to the url*/
  target?: AnchorLinkTargets;
  /** Text on close-button in service messages. */
  closeBtnText?: string;
  /** Changes the visual representation. */
  variant?: NotificationPanelVariants;
  /** Sets the role of the service message - default: auto, will decide based on variant */
  messageRole?: ServiceMessageRoles;
  /** Sets the data-testid attribute. */
  testId?: string;
}

const ServiceMessage: React.FC<ServiceMessageProps> = ({
  label,
  dismissable = true,
  onDismiss,
  info,
  extraInfo,
  urlTitle,
  url,
  target = '_self',
  closeBtnText = 'fjern melding',
  expanderOpenFromStart = false,
  variant = 'error',
  messageRole = 'auto',
  testId,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(expanderOpenFromStart);

  const labelId = useId();
  const hasExpander = !!info || !!extraInfo;

  const autoRole = variant === 'error' ? 'alert' : 'region';
  const ariaRole = messageRole === 'auto' ? autoRole : messageRole === 'none' ? undefined : messageRole;
  const ariaLabelAttributes = getAriaLabelAttributes({ label, id: labelId });

  const handleClick = (): void => {
    if (hasExpander) setIsExpanded(!isExpanded);
  };

  const classes = classNames(styles['service-message'], styles[`service-message--${variant}`]);

  return (
    <div className={classes} role={ariaRole} {...ariaLabelAttributes} data-testid={testId}>
      <Label
        label={label}
        variant={variant}
        id={labelId}
        hasExpander={hasExpander}
        isExpanded={isExpanded}
        dismissable={dismissable}
        onExpand={handleClick}
        onDismiss={onDismiss}
        closeBtnText={closeBtnText}
      />
      {hasExpander && isExpanded && (
        <Content
          info={info}
          extraInfo={extraInfo}
          urlTitle={urlTitle}
          url={url}
          target={target}
          dismissable={dismissable}
          onDismiss={onDismiss}
          closeBtnText={closeBtnText}
        />
      )}
    </div>
  );
};

export default ServiceMessage;
